import React, { useState, useEffect, useContext } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress, Link, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, FormControlLabel, Checkbox, Container, Stack, Divider, Chip } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LeadService } from '../../services/lead.service';
import bgImage from '../../assets/images/bg-app-4.jpg';
import DoneIcon from '@mui/icons-material/Done';
import tacticaLogo from '../../assets/images/tacticaLogov3.png';
import { useTranslation } from '../../i18n';
import { LangContext } from '../../landing/index';
import { Language, Country } from '../../types/auth.types';

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  club: string;
  phone: string;
  password: string;
  marketingSource: string;
  marketingConsent: boolean;
  confirmPassword: string;
  country: Country;
  language: Language;
}

interface FormErrors {
  email?: string;
  firstName?: string;
  lastName?: string;
  club?: string;
  phone?: string;
  password?: string;
  marketingSource?: string;
  marketingConsent?: string;
  country?: string;
  language?: string;
}

interface MultiStepSignUpFormProps {
  onError: (error: string) => void;
}

const MARKETING_SOURCES = [
  'Google',
  'YouTube',
  'TikTok',
  'Instagram',
  'Facebook',
  'Bouche à oreille',
  'Autre'
];

const COUNTRIES = [
  { code: 'FR', label: 'France' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'ES', label: 'Spain' },
  { code: 'RU', label: 'Russia' },
  { code: 'BE', label: 'Belgium' },
  { code: 'CH', label: 'Switzerland' },
  { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' },
  { code: 'OTHER', label: 'Other' }
];

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'ru', label: 'Русский' }
];

export const MultiStepSignUpForm: React.FC<MultiStepSignUpFormProps> = ({ onError }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const invitedEmail = params.get('email');
  const { lang } = useContext(LangContext);
  const { t } = useTranslation(lang);
  const { signUp } = useAuth();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    email: '',
    firstName: '',
    lastName: '',
    club: '',
    phone: '',
    password: '',
    marketingSource: '',
    marketingConsent: false,
    confirmPassword: '',
    country: 'FR' as Country,
    language: 'fr' as Language
  });
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Éviter les problèmes de page blanche
  useEffect(() => {
    // Forcer la couleur de fond sur le body et html
    document.body.style.backgroundColor = '#1a1a1a';
    document.documentElement.style.backgroundColor = '#1a1a1a';
    
    // Ajouter un style global pour masquer les anciens fonds fixes problématiques (mais laisser le nouveau fond vidéo)
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      div[style*="background: linear-gradient"][style*="position: fixed"] {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(styleElement);
    
    return () => {
      // Nettoyer lors du démontage
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    if (invitedEmail) {
      try {
        const decodedEmail = atob(invitedEmail);
        if (decodedEmail === invitedEmail) {
          setFormData(prev => ({
            ...prev,
            email: invitedEmail
          }));
        } else {
          window.location.href = 'https://tacticafoot.com';
        }
      } catch (error) {
        window.location.href = 'https://tacticafoot.com';
      }
    }
  }, [invitedEmail]);

  const validateStep1 = () => {
    const errors: FormErrors = {};
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!formData.email) {
      errors.email = t('signup.errors.required');
    } else if (!emailRegex.test(formData.email)) {
      errors.email = t('signup.errors.invalidEmail') || 'Veuillez entrer une adresse email valide';
    }

    if (!formData.firstName) {
      errors.firstName = t('signup.errors.required');
    }
    if (!formData.marketingSource) {
      errors.marketingSource = t('signup.errors.required');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors: { [key: string]: string } = {};
    
    if (!formData.password) {
      errors.password = t('signup.errors.required');
    }
    if (!formData.lastName) {
      errors.lastName = t('signup.errors.required');
    }
    if (!formData.club) {
      errors.club = t('signup.errors.required');
    }
    if (!formData.phone) {
      errors.phone = t('signup.errors.required');
    }
    if (!formData.country) {
      errors.country = t('signup.errors.required');
    }
    if (!formData.language) {
      errors.language = t('signup.errors.required');
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (step === 1) {
      if (!validateStep1()) return;
      
      try {
        await LeadService.saveLead({
          email: formData.email,
          firstName: formData.firstName,
          step: 1,
          timestamp: new Date(),
          marketingSource: formData.marketingSource
        });
        setStep(2);
      } catch (error: any) {
        setError(t('signup.errors.registration'));
      }
      return;
    }

    setIsLoading(true);

    try {
      await signUp(
        formData.email,
        formData.password,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          club: formData.club,
          phone: formData.phone,
          marketingSource: formData.marketingSource,
          marketingConsent: formData.marketingConsent,
          country: formData.country,
          language: formData.language
        }
      );
      
      setShowConfirmation(true);
      
      setTimeout(() => {
        navigate('/tutorial');
      }, 2500);
    } catch (error: any) {
      if (error.code === 'auth/email-already-in-use') {
        setError(t('signup.errors.emailInUse'));
      } else {
        setError(t('signup.errors.registration'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      marketingConsent: event.target.checked
    });
  };

  const renderStep1 = () => (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 2, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        👤 Vos informations
      </Typography>
      <TextField
        margin="normal"
        fullWidth
        id="email"
        label="Adresse email"
        name="email"
        autoComplete="email"
        placeholder="votre.email@exemple.com"
        value={formData.email}
        onChange={handleTextChange}
        disabled={!!invitedEmail}
        error={!!formErrors.email}
        helperText={formErrors.email}
        InputProps={{ required: true }}
        sx={{ 
          '& .MuiOutlinedInput-root': { 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& input': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.75)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
            fontWeight: 700
          }
        }}
      />
      <TextField
        margin="normal"
        fullWidth
        id="firstName"
        label="Prénom"
        name="firstName"
        autoComplete="given-name"
        placeholder="Jean"
        value={formData.firstName}
        onChange={handleTextChange}
        error={!!formErrors.firstName}
        helperText={formErrors.firstName}
        InputProps={{ required: true }}
        sx={{ 
          '& .MuiOutlinedInput-root': { 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& input': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.75)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
            fontWeight: 700
          }
        }}
      />
      
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mt: 3, mb: 2, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        📊 Comment nous avez-vous connu ?
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel id="marketing-source-label" sx={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(0,0,0,0.75)', '&.Mui-focused': { color: '#1976d2', fontWeight: 700 } }}>Source de découverte</InputLabel>
        <Select
          labelId="marketing-source-label"
          id="marketing-source"
          name="marketingSource"
          value={formData.marketingSource}
          label="Source de découverte"
          onChange={handleSelectChange}
          error={!!formErrors.marketingSource}
          inputProps={{ required: true }}
          sx={{ 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& .MuiSelect-select': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          }}
        >
          {MARKETING_SOURCES.map((source) => (
            <MenuItem key={source} value={source}>
              {source}
            </MenuItem>
          ))}
        </Select>
        {formErrors.marketingSource && (
          <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
            {formErrors.marketingSource}
          </Typography>
        )}
      </FormControl>
      
      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(25,118,210,0.08)', borderRadius: 2, border: '1px solid rgba(25,118,210,0.25)' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.marketingConsent}
              onChange={handleCheckboxChange}
              name="marketingConsent"
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.75)' }}>
              J'accepte de recevoir des conseils et actualités TacticaFoot par email (optionnel)
            </Typography>
          }
        />
      </Box>
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        sx={{ 
          mt: 4, 
          py: 1.5,
          bgcolor: '#2e7d32',
          '&:hover': { bgcolor: '#1b5e20' },
          borderRadius: 2,
          fontWeight: 700,
          fontSize: '1rem',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(46,125,50,0.3)'
        }}
        disabled={isLoading}
      >
        {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Continuer →'}
      </Button>
    </Box>
  );

  const renderStep2 = () => (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mb: 2, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        ⚙️ Complétez votre profil
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          margin="normal"
          fullWidth
          id="lastName"
          label="Nom"
          name="lastName"
          autoComplete="family-name"
          placeholder="Dupont"
          value={formData.lastName}
          onChange={handleTextChange}
          error={!!formErrors.lastName}
          helperText={formErrors.lastName}
          InputProps={{ required: true }}
          sx={{ 
          '& .MuiOutlinedInput-root': { 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& input': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.75)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
            fontWeight: 700
          }
        }}
        />
        <TextField
          margin="normal"
          fullWidth
          id="phone"
          label="Téléphone"
          name="phone"
          autoComplete="tel"
          placeholder="+33 6 12 34 56 78"
          value={formData.phone}
          onChange={handleTextChange}
          error={!!formErrors.phone}
          helperText={formErrors.phone}
          InputProps={{ required: true }}
          sx={{ 
          '& .MuiOutlinedInput-root': { 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& input': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.75)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
            fontWeight: 700
          }
        }}
        />
      </Stack>
      
      <TextField
        margin="normal"
        fullWidth
        id="club"
        label="Club / Équipe"
        name="club"
        placeholder="AS Exemple FC"
        value={formData.club}
        onChange={handleTextChange}
        error={!!formErrors.club}
        helperText={formErrors.club || 'Le nom de votre club ou équipe'}
        FormHelperTextProps={{ sx: { color: 'rgba(0,0,0,0.6)' } }}
        InputProps={{ required: true }}
        sx={{ 
          '& .MuiOutlinedInput-root': { 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& input': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.75)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
            fontWeight: 700
          }
        }}
      />
      
      <TextField
        margin="normal"
        fullWidth
        name="password"
        label="Mot de passe"
        type="password"
        id="password"
        autoComplete="new-password"
        placeholder="Minimum 6 caractères"
        value={formData.password}
        onChange={handleTextChange}
        error={!!formErrors.password}
        helperText={formErrors.password || 'Choisissez un mot de passe sécurisé'}
        FormHelperTextProps={{ sx: { color: 'rgba(0,0,0,0.6)' } }}
        InputProps={{ required: true }}
        sx={{ 
          '& .MuiOutlinedInput-root': { 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& fieldset': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& input': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          },
          '& .MuiInputLabel-root': {
            fontSize: '1rem',
            fontWeight: 600,
            color: 'rgba(0,0,0,0.75)'
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
            fontWeight: 700
          }
        }}
      />
      
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a237e', mt: 3, mb: 2, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        🌍 Préférences
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <FormControl fullWidth margin="normal">
          <InputLabel id="country-label" sx={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(0,0,0,0.75)', '&.Mui-focused': { color: '#1976d2', fontWeight: 700 } }}>Pays</InputLabel>
          <Select
            labelId="country-label"
            id="country"
            name="country"
            value={formData.country}
            label="Pays"
            onChange={handleSelectChange}
            error={!!formErrors.country}
            sx={{ 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& .MuiSelect-select': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          }}
          >
            {COUNTRIES.map((country) => (
              <MenuItem key={country.code} value={country.code as Country}>
                {country.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="normal">
          <InputLabel id="language-label" sx={{ fontSize: '1rem', fontWeight: 600, color: 'rgba(0,0,0,0.75)', '&.Mui-focused': { color: '#1976d2', fontWeight: 700 } }}>Langue</InputLabel>
          <Select
            labelId="language-label"
            id="language"
            name="language"
            value={formData.language}
            label="Langue"
            onChange={handleSelectChange}
            error={!!formErrors.language}
            sx={{ 
            borderRadius: 2, 
            bgcolor: 'white',
            fontSize: '1rem',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.23)',
              borderWidth: '1.5px'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0,0,0,0.4)'
            },
            '& .MuiSelect-select': {
              color: 'rgba(0,0,0,0.95)',
              fontWeight: 500
            }
          }}
          >
            {LANGUAGES.map((lang) => (
              <MenuItem key={lang.code} value={lang.code as Language}>
                {lang.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      
      <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={() => setStep(1)}
          sx={{ 
            flex: 1,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 600,
            textTransform: 'none',
            borderColor: 'rgba(0,0,0,0.23)',
            color: 'rgba(0,0,0,0.7)'
          }}
        >
          ← Retour
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{ 
            flex: 2,
            py: 1.5,
            bgcolor: '#2e7d32',
            '&:hover': { bgcolor: '#1b5e20' },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: '1rem',
            textTransform: 'none',
            boxShadow: '0 4px 12px rgba(46,125,50,0.3)'
          }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Créer mon compte 🎉'}
        </Button>
      </Stack>
    </Box>
  );

  if (showConfirmation) {
    return (
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        width: '100%',
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        p: 4
      }}>
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mt: 8 }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            {t('signup.success.title')}
          </Typography>
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <DoneIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {t('signup.success.message')}
            </Typography>
            <Button
              variant="contained"
              fullWidth
              sx={{ mt: 3 }}
              onClick={() => navigate('/tutorial')}
            >
              {t('signup.success.cta')}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
      backgroundImage: `url(${bgImage})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      bgcolor: '#1a1a1a',
      py: { xs: 4, md: 8 }
    }}>
      <Container maxWidth="sm">
        <Paper elevation={6} sx={{ 
          p: { xs: 3, md: 5 }, 
          borderRadius: 3,
          background: 'linear-gradient(145deg, #f5f7fa 0%, #e8eaf0 100%)',
          border: '1px solid rgba(0,0,0,0.12)'
        }}>
          <Box 
            component="img"
            src={tacticaLogo}
            alt="Tactica Logo"
            sx={{
              width: '100px',
              height: 'auto',
              display: 'block',
              margin: '0 auto 24px'
            }}
          />
          
          <Typography 
            component="h1" 
            variant="h4" 
            align="center" 
            sx={{ 
              fontWeight: 800,
              color: '#1a237e',
              mb: 1
            }}
          >
            Découvrir TacticaFoot
          </Typography>
          
          
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {step === 1 ? renderStep1() : renderStep2()}
          
          {/* <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 4, mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Chip label="✅ Aucune carte bancaire" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, fontSize: '0.75rem', border: '1px solid #a5d6a7' }} />
            <Chip label="⚡ Accès immédiat" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 700, fontSize: '0.75rem', border: '1px solid #90caf9' }} />
            <Chip label="🔒 Données sécurisées" size="small" sx={{ bgcolor: '#f5f5f5', color: 'rgba(0,0,0,0.87)', fontWeight: 700, fontSize: '0.75rem', border: '1px solid rgba(0,0,0,0.23)' }} />
          </Stack> */}
          
          <Divider sx={{ my: 4 }} />
          
          <Typography align="center" variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500 }}>
            Déjà un compte ?{' '}
            <Link href="https://app.tacticafoot.com/connexion" sx={{ fontWeight: 600, color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Se connecter
            </Link>
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};
